import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from '@iptv/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, Save } from 'lucide-react';
import { useEffect, useState } from 'react';

import { adminFetch } from '../lib/api';
import { useCurrentAdmin } from '../lib/auth';

/**
 * Operator settings page. Three cards:
 *
 *   1. Account (read-only — who am I)
 *   2. IPTV provisioning (per-site provider selection + dynamic config form)
 *   3. Customer messaging (per-site email + WhatsApp templates, EN/ES tabs)
 *
 * Cards 2 + 3 are per-site, so they share a Site picker at the top.
 *
 * The IPTV card is provider-agnostic: it reads
 * `GET /admin/iptv/providers` once on mount to get the catalogue
 * (`{code, display_name, config_schema}` per provider). The selected
 * provider's `config_schema` (a JSON Schema fragment) drives the form
 * fields rendered underneath the dropdown — adding a new provider on
 * the backend automatically surfaces it here, no UI redeploy needed.
 */

interface SiteRef {
  id: number;
  slug: string;
}

interface ProviderCatalogueEntry {
  code: string;
  display_name: string;
  config_schema: {
    properties?: Record<string, JsonSchemaField>;
    required?: string[];
  } | null;
}

interface JsonSchemaField {
  type?: string | string[];
  description?: string;
  default?: unknown;
  is_secret?: boolean;
  [key: string]: unknown;
}

interface IptvSettings {
  active_provider: string;
  trial_mode: 'trial_24h' | 'live_full';
  providers: Record<string, Record<string, unknown>>;
}

interface MessagingSettings {
  email: {
    enabled: boolean;
    from_address: string;
    from_name: string;
    // `Reply-To` header. Blank → omit (customers reply to the From
    // address). Use a separate reply-to when the From is a no-reply
    // sender configured for deliverability but support replies need
    // to land in a real shared mailbox.
    reply_to: string;
    // Blind-copied on every customer-facing email so an internal
    // mailbox keeps a silent audit trail. Multiple addresses are
    // supported comma-separated. Python's smtplib strips the Bcc
    // header from the on-wire message so the customer never sees it.
    bcc: string;
    subject: { en: string; es: string };
    body_html: { en: string; es: string };
    smtp: {
      host: string;
      port: number | null;
      user: string;
      password: string;
      use_tls: boolean;
    };
  };
  whatsapp: {
    enabled: boolean;
    sender_number: string;
    message_template: { en: string; es: string };
  };
}

const EMPTY_IPTV: IptvSettings = {
  active_provider: 'manual',
  trial_mode: 'trial_24h',
  providers: {},
};

const EMPTY_MESSAGING: MessagingSettings = {
  email: {
    enabled: true,
    from_address: '',
    from_name: '',
    reply_to: '',
    bcc: '',
    subject: { en: '', es: '' },
    body_html: { en: '', es: '' },
    smtp: {
      host: '',
      port: null,
      user: '',
      password: '',
      use_tls: true,
    },
  },
  whatsapp: {
    enabled: true,
    sender_number: '',
    message_template: { en: '', es: '' },
  },
};

export function SettingsPage() {
  const me = useCurrentAdmin();
  const [siteId, setSiteId] = useState<number | null>(null);

  const sites = useQuery({
    queryKey: ['admin', 'sites'],
    queryFn: () => adminFetch<SiteRef[]>('/api/v1/admin/sites'),
  });

  useEffect(() => {
    const first = sites.data?.[0];
    if (siteId === null && first) {
      setSiteId(first.id);
    }
  }, [sites.data, siteId]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div>
            <span className="text-muted-foreground">Email:</span> {me.data?.email}
          </div>
          <div>
            <span className="text-muted-foreground">Role:</span> {me.data?.role}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Label htmlFor="site-picker" className="text-sm text-muted-foreground">
          Site:
        </Label>
        <select
          id="site-picker"
          value={siteId ?? ''}
          onChange={(e) => setSiteId(Number(e.target.value))}
          className="rounded-md border bg-background px-2 py-1 text-sm"
        >
          {sites.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.slug}
            </option>
          ))}
        </select>
        {siteId !== null && (
          <Badge variant="secondary" className="text-[10px]">
            site_id #{siteId}
          </Badge>
        )}
      </div>

      {siteId !== null && (
        <>
          <SupportContactCard siteId={siteId} />
          <IptvCard siteId={siteId} />
          <MessagingCard siteId={siteId} />
          <I18nCard siteId={siteId} />
        </>
      )}
    </div>
  );
}

/* ─── Per-site public-support WhatsApp number ──────────────────────── */

interface SupportContactSettings {
  whatsapp_e164: string;
  whatsapp_label: string;
}

function SupportContactCard({ siteId }: { siteId: number }) {
  const qc = useQueryClient();
  const current = useQuery({
    queryKey: ['admin', 'support-contact', siteId],
    queryFn: () =>
      adminFetch<SupportContactSettings>(`/api/v1/admin/sites/${siteId}/settings/support`),
  });
  const [draft, setDraft] = useState<SupportContactSettings>({
    whatsapp_e164: '',
    whatsapp_label: '',
  });
  useEffect(() => {
    if (current.data) setDraft(current.data);
  }, [current.data]);

  const save = useMutation({
    mutationFn: (payload: SupportContactSettings) =>
      adminFetch<SupportContactSettings>(`/api/v1/admin/sites/${siteId}/settings/support`, {
        method: 'PUT',
        body: payload,
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['admin', 'support-contact', siteId] });
      setDraft(data);
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Public-site support contact</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          One canonical place to edit the WhatsApp number shown on the public marketing site. Saving
          here propagates the value to every CMS field the public components still read from —
          branding badge, footer, floating button, exit-intent popup, final-CTA “WhatsApp Support”
          link. No frontend redeploy required; the new number lands on the next 60-second
          revalidation of <code>/api/v1/sites/&lt;slug&gt;/config</code>.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="support-wa-e164">WhatsApp number (E.164)</Label>
            <Input
              id="support-wa-e164"
              placeholder="+19295667182"
              value={draft.whatsapp_e164}
              onChange={(e) => setDraft({ ...draft, whatsapp_e164: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              International format with the leading <code>+</code>. Used for the <code>wa.me/</code>
              link target (digits only).
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="support-wa-label">Display label (optional)</Label>
            <Input
              id="support-wa-label"
              placeholder="+1 929 566 7182"
              value={draft.whatsapp_label}
              onChange={(e) => setDraft({ ...draft, whatsapp_label: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Spaced human-friendly form shown alongside the icon. Leave blank to auto-format from
              the E.164 value above.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          {save.isPending && <span className="text-xs text-muted-foreground">Saving…</span>}
          {save.isSuccess && !save.isPending && (
            <span className="text-xs text-emerald-600">Saved.</span>
          )}
          {save.isError && (
            <span className="text-xs text-destructive">
              {(save.error as Error)?.message ?? 'Save failed'}
            </span>
          )}
          <Button onClick={() => save.mutate(draft)} disabled={save.isPending}>
            <Save className="mr-1.5 h-3.5 w-3.5" />
            Save support contact
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ─── Per-site i18n card — enabled languages ───────────────────────── */

interface I18nSettings {
  enabled_locales: string[];
}

const ALL_LOCALES: { code: string; native: string; flag: string }[] = [
  { code: 'en', native: 'English', flag: '🇬🇧' },
  { code: 'fr', native: 'Français', flag: '🇫🇷' },
  { code: 'it', native: 'Italiano', flag: '🇮🇹' },
  { code: 'nl', native: 'Nederlands', flag: '🇳🇱' },
  { code: 'es', native: 'Español', flag: '🇪🇸' },
  { code: 'de', native: 'Deutsch', flag: '🇩🇪' },
];

const EMPTY_I18N: I18nSettings = {
  enabled_locales: ['en'],
};

function I18nCard({ siteId }: { siteId: number }) {
  const qc = useQueryClient();
  const current = useQuery({
    queryKey: ['admin', 'i18n-settings', siteId],
    queryFn: () => adminFetch<I18nSettings>(`/api/v1/admin/sites/${siteId}/settings/i18n`),
  });

  const [draft, setDraft] = useState<I18nSettings>(EMPTY_I18N);
  useEffect(() => {
    if (current.data) setDraft(current.data);
  }, [current.data]);

  const save = useMutation({
    mutationFn: (payload: I18nSettings) =>
      adminFetch<I18nSettings>(`/api/v1/admin/sites/${siteId}/settings/i18n`, {
        method: 'PUT',
        body: payload,
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['admin', 'i18n-settings', siteId] });
      setDraft(data);
    },
  });

  function toggle(code: string) {
    // `en` is the baseline / hydration locale — it's force-enabled
    // server-side anyway, but lock the checkbox so the operator
    // doesn't get a confusing "saved but EN came back" round-trip.
    if (code === 'en') return;
    setDraft((d) => {
      const set = new Set(d.enabled_locales);
      if (set.has(code)) set.delete(code);
      else set.add(code);
      return { enabled_locales: ALL_LOCALES.map((l) => l.code).filter((c) => set.has(c)) };
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Public-site languages</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Tick which languages the public site should expose in its language picker. English is
          always on (it’s the baseline used for SSR + as the fallback when a translation is
          missing). Disabling a language here removes it from the picker without a redeploy — the
          next request to the public site sees the change.
        </p>
        <div className="grid gap-2 md:grid-cols-2">
          {ALL_LOCALES.map((loc) => {
            const checked = draft.enabled_locales.includes(loc.code);
            const isBaseline = loc.code === 'en';
            return (
              <label
                key={loc.code}
                className={`flex items-center gap-3 rounded-md border p-3 text-sm transition ${
                  isBaseline
                    ? 'cursor-not-allowed opacity-90'
                    : 'cursor-pointer hover:bg-secondary/50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={isBaseline}
                  onChange={() => toggle(loc.code)}
                />
                <span className="text-base">{loc.flag}</span>
                <span className="font-medium">{loc.native}</span>
                <span className="ml-auto text-[11px] uppercase tracking-wider text-muted-foreground">
                  {loc.code}
                </span>
                {isBaseline && (
                  <span className="text-[10px] uppercase tracking-wider text-emerald-600">
                    always on
                  </span>
                )}
              </label>
            );
          })}
        </div>
        <div className="flex items-center justify-end gap-2">
          {save.isPending && <span className="text-xs text-muted-foreground">Saving…</span>}
          {save.isSuccess && !save.isPending && (
            <span className="text-xs text-emerald-600">Saved.</span>
          )}
          {save.isError && (
            <span className="text-xs text-destructive">
              {(save.error as Error)?.message ?? 'Save failed'}
            </span>
          )}
          <Button onClick={() => save.mutate(draft)} disabled={save.isPending}>
            <Save className="mr-1.5 h-3.5 w-3.5" />
            Save language settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function IptvCard({ siteId }: { siteId: number }) {
  const qc = useQueryClient();
  const [revealKey, setRevealKey] = useState<Record<string, boolean>>({});

  const catalogue = useQuery({
    queryKey: ['admin', 'iptv', 'providers'],
    queryFn: () => adminFetch<ProviderCatalogueEntry[]>('/api/v1/admin/iptv/providers'),
    staleTime: 60_000,
  });

  const current = useQuery({
    queryKey: ['admin', 'iptv-settings', siteId],
    queryFn: () => adminFetch<IptvSettings>(`/api/v1/admin/sites/${siteId}/settings/iptv`),
  });

  const [draft, setDraft] = useState<IptvSettings>(EMPTY_IPTV);
  useEffect(() => {
    if (current.data) setDraft(current.data);
  }, [current.data]);

  const save = useMutation({
    mutationFn: (payload: IptvSettings) =>
      adminFetch<IptvSettings>(`/api/v1/admin/sites/${siteId}/settings/iptv`, {
        method: 'PUT',
        body: payload,
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['admin', 'iptv-settings', siteId] });
      setDraft(data);
    },
  });

  const selectedProvider = catalogue.data?.find((p) => p.code === draft.active_provider);
  const providerConfig = draft.providers[draft.active_provider] ?? {};

  function updateProviderField(field: string, value: unknown) {
    setDraft({
      ...draft,
      providers: {
        ...draft.providers,
        [draft.active_provider]: {
          ...providerConfig,
          [field]: value,
        },
      },
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">IPTV provisioning</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="iptv-provider">Active provider</Label>
            <select
              id="iptv-provider"
              value={draft.active_provider}
              onChange={(e) => setDraft({ ...draft, active_provider: e.target.value })}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {catalogue.data?.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.display_name} ({p.code})
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Adding a new provider class in the backend automatically surfaces it here.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="trial-mode">Trial mode</Label>
            <select
              id="trial-mode"
              value={draft.trial_mode}
              onChange={(e) =>
                setDraft({ ...draft, trial_mode: e.target.value as IptvSettings['trial_mode'] })
              }
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="trial_24h">trial_24h — force 24h trial for every paid order</option>
              <option value="live_full">live_full — use the plan&apos;s mapped package</option>
            </select>
            <p className="text-xs text-muted-foreground">
              Keep on <code>trial_24h</code> until you&apos;ve validated end-to-end with at least
              one real paid order.
            </p>
          </div>
        </div>

        {selectedProvider?.config_schema?.properties && (
          <div className="space-y-3 rounded-md border border-dashed p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              {selectedProvider.display_name} configuration
            </div>
            {Object.entries(selectedProvider.config_schema.properties).map(([field, spec]) => {
              const isSecret = Boolean(spec.is_secret);
              const isHidden = isSecret && !revealKey[field];
              const type = Array.isArray(spec.type) ? spec.type[0] : spec.type;
              const value = providerConfig[field];

              if (type === 'boolean') {
                return (
                  <label key={field} className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={Boolean(value)}
                      onChange={(e) => updateProviderField(field, e.target.checked)}
                      className="mt-1"
                    />
                    <span>
                      <span className="font-medium">{field}</span>
                      {spec.description && (
                        <span className="block text-xs text-muted-foreground">
                          {spec.description}
                        </span>
                      )}
                    </span>
                  </label>
                );
              }

              if (type === 'object') {
                let display = '';
                try {
                  display =
                    typeof value === 'string'
                      ? value
                      : JSON.stringify(value ?? spec.default ?? {}, null, 2);
                } catch {
                  display = String(value ?? '');
                }
                return (
                  <div key={field} className="space-y-1.5">
                    <Label htmlFor={`iptv-${field}`}>{field}</Label>
                    <textarea
                      id={`iptv-${field}`}
                      rows={4}
                      value={display}
                      onChange={(e) => {
                        try {
                          updateProviderField(field, JSON.parse(e.target.value));
                        } catch {
                          updateProviderField(field, e.target.value);
                        }
                      }}
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                    />
                    {spec.description && (
                      <p className="text-xs text-muted-foreground">{spec.description}</p>
                    )}
                  </div>
                );
              }

              return (
                <div key={field} className="space-y-1.5">
                  <Label htmlFor={`iptv-${field}`}>
                    {field} {isSecret && <span className="text-amber-600">(secret)</span>}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id={`iptv-${field}`}
                      type={isHidden ? 'password' : type === 'integer' ? 'number' : 'text'}
                      value={value === undefined || value === null ? '' : String(value)}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const coerced = type === 'integer' && raw !== '' ? Number(raw) : raw;
                        updateProviderField(field, coerced);
                      }}
                    />
                    {isSecret && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setRevealKey((prev) => ({ ...prev, [field]: !prev[field] }))}
                      >
                        {revealKey[field] ? (
                          <EyeOff className="h-3.5 w-3.5" />
                        ) : (
                          <Eye className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    )}
                  </div>
                  {spec.description && (
                    <p className="text-xs text-muted-foreground">{spec.description}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          {save.isPending && <span className="text-xs text-muted-foreground">Saving…</span>}
          {save.isSuccess && !save.isPending && (
            <span className="text-xs text-emerald-600">Saved.</span>
          )}
          {save.isError && (
            <span className="text-xs text-destructive">
              {(save.error as Error)?.message ?? 'Save failed'}
            </span>
          )}
          <Button onClick={() => save.mutate(draft)} disabled={save.isPending}>
            <Save className="mr-1.5 h-3.5 w-3.5" />
            Save IPTV settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function MessagingCard({ siteId }: { siteId: number }) {
  const qc = useQueryClient();
  const [lang, setLang] = useState<'en' | 'es'>('en');
  const [revealSmtpPassword, setRevealSmtpPassword] = useState(false);

  const current = useQuery({
    queryKey: ['admin', 'messaging-settings', siteId],
    queryFn: () =>
      adminFetch<MessagingSettings>(`/api/v1/admin/sites/${siteId}/settings/messaging`),
  });

  const [draft, setDraft] = useState<MessagingSettings>(EMPTY_MESSAGING);
  useEffect(() => {
    if (current.data) setDraft(current.data);
  }, [current.data]);

  const save = useMutation({
    mutationFn: (payload: MessagingSettings) =>
      adminFetch<MessagingSettings>(`/api/v1/admin/sites/${siteId}/settings/messaging`, {
        method: 'PUT',
        body: payload,
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['admin', 'messaging-settings', siteId] });
      setDraft(data);
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Customer messaging</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Master toggles — gate the whole channel per-site. When off,
            deliver_subscription_credentials skips that channel entirely.
            Useful for: WhatsApp-only tenants, debugging deliverability,
            or temporarily silencing a broken channel without breaking
            the rest of the post-payment pipeline. */}
        <div className="grid gap-3 rounded-md border bg-secondary/30 p-3 md:grid-cols-2">
          <label htmlFor="msg-email-enabled" className="flex items-start gap-2 text-sm">
            <input
              id="msg-email-enabled"
              type="checkbox"
              checked={draft.email.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  email: { ...draft.email, enabled: e.target.checked },
                })
              }
              className="mt-1"
            />
            <span className="font-medium">
              Send email on subscription
              <span className="block text-xs font-normal text-muted-foreground">
                When off, SMTP send is skipped for this site.
              </span>
            </span>
          </label>
          <label htmlFor="msg-wa-enabled" className="flex items-start gap-2 text-sm">
            <input
              id="msg-wa-enabled"
              type="checkbox"
              checked={draft.whatsapp.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  whatsapp: { ...draft.whatsapp, enabled: e.target.checked },
                })
              }
              className="mt-1"
            />
            <span className="font-medium">
              Send WhatsApp on subscription
              <span className="block text-xs font-normal text-muted-foreground">
                When off, no Twilio/Meta push and no wa.me link is built.
              </span>
            </span>
          </label>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="email-from-address">Email from address</Label>
            <Input
              id="email-from-address"
              type="email"
              placeholder="support@kickofftv26.com"
              value={draft.email.from_address}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  email: { ...draft.email, from_address: e.target.value },
                })
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email-from-name">Email from name</Label>
            <Input
              id="email-from-name"
              placeholder="KickoffTV Support"
              value={draft.email.from_name}
              onChange={(e) =>
                setDraft({ ...draft, email: { ...draft.email, from_name: e.target.value } })
              }
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="email-reply-to">Reply-To address</Label>
            <Input
              id="email-reply-to"
              type="email"
              placeholder="support@kickofftv26.com"
              value={draft.email.reply_to}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  email: { ...draft.email, reply_to: e.target.value },
                })
              }
            />
            <p className="text-xs text-muted-foreground">
              Optional. When set, customer replies go here instead of the From address — useful when
              the From is a no-reply sender configured for deliverability. Leave blank to let
              replies go back to the From address.
            </p>
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="email-bcc">BCC address (audit copy)</Label>
            <Input
              id="email-bcc"
              type="text"
              placeholder="ops@kickofftv26.com"
              value={draft.email.bcc}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  email: { ...draft.email, bcc: e.target.value },
                })
              }
            />
            <p className="text-xs text-muted-foreground">
              Optional. Every credentials email is blind-copied to this address so you keep an audit
              trail of what landed in customer inboxes. The customer never sees it (smtplib strips
              the Bcc header before sending). Comma-separated for multiple addresses.
            </p>
          </div>
        </div>

        {/* SMTP relay — per-site override; empty fields fall back to
            the global SMTP_* env vars on the backend. */}
        <div className="space-y-3 rounded-md border border-dashed p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            SMTP relay (per-site override)
          </div>
          <p className="text-xs text-muted-foreground">
            Leave any field blank to inherit the corresponding <code>SMTP_*</code> env var on the
            backend.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="smtp-host">Host</Label>
              <Input
                id="smtp-host"
                placeholder="smtp.brevo.com"
                value={draft.email.smtp.host}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    email: {
                      ...draft.email,
                      smtp: { ...draft.email.smtp, host: e.target.value },
                    },
                  })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtp-port">Port</Label>
              <Input
                id="smtp-port"
                type="number"
                placeholder="587"
                value={draft.email.smtp.port ?? ''}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    email: {
                      ...draft.email,
                      smtp: {
                        ...draft.email.smtp,
                        port: e.target.value === '' ? null : Number(e.target.value),
                      },
                    },
                  })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtp-user">User</Label>
              <Input
                id="smtp-user"
                placeholder="your-smtp-login"
                value={draft.email.smtp.user}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    email: {
                      ...draft.email,
                      smtp: { ...draft.email.smtp, user: e.target.value },
                    },
                  })
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtp-password">
                Password <span className="text-amber-600">(secret)</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="smtp-password"
                  type={revealSmtpPassword ? 'text' : 'password'}
                  value={draft.email.smtp.password}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      email: {
                        ...draft.email,
                        smtp: { ...draft.email.smtp, password: e.target.value },
                      },
                    })
                  }
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setRevealSmtpPassword(!revealSmtpPassword)}
                >
                  {revealSmtpPassword ? (
                    <EyeOff className="h-3.5 w-3.5" />
                  ) : (
                    <Eye className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.email.smtp.use_tls}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  email: {
                    ...draft.email,
                    smtp: { ...draft.email.smtp, use_tls: e.target.checked },
                  },
                })
              }
            />
            <span>STARTTLS</span>
          </label>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="wa-sender">WhatsApp sender number (E.164)</Label>
          <Input
            id="wa-sender"
            placeholder="+212612345678"
            value={draft.whatsapp.sender_number}
            onChange={(e) =>
              setDraft({
                ...draft,
                whatsapp: { ...draft.whatsapp, sender_number: e.target.value },
              })
            }
          />
          <p className="text-xs text-muted-foreground">
            Used as the support number in <code>wa.me</code> links + as the From in any future
            WhatsApp API integration.
          </p>
        </div>

        <div className="space-y-3 rounded-md border p-4">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              Templates
            </span>
            <div className="ml-auto flex items-center gap-1">
              {(['en', 'es'] as const).map((l) => (
                <Button
                  key={l}
                  size="sm"
                  variant={lang === l ? 'default' : 'outline'}
                  onClick={() => setLang(l)}
                >
                  {l.toUpperCase()}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`email-subject-${lang}`}>Email subject</Label>
            <Input
              id={`email-subject-${lang}`}
              value={draft.email.subject[lang]}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  email: {
                    ...draft.email,
                    subject: { ...draft.email.subject, [lang]: e.target.value },
                  },
                })
              }
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`email-body-${lang}`}>Email body (HTML)</Label>
            <textarea
              id={`email-body-${lang}`}
              rows={10}
              value={draft.email.body_html[lang]}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  email: {
                    ...draft.email,
                    body_html: { ...draft.email.body_html, [lang]: e.target.value },
                  },
                })
              }
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`wa-template-${lang}`}>WhatsApp message</Label>
            <textarea
              id={`wa-template-${lang}`}
              rows={6}
              value={draft.whatsapp.message_template[lang]}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  whatsapp: {
                    ...draft.whatsapp,
                    message_template: {
                      ...draft.whatsapp.message_template,
                      [lang]: e.target.value,
                    },
                  },
                })
              }
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-xs"
            />
          </div>

          <p className="text-[11px] text-muted-foreground">
            Available placeholders:{' '}
            <code>
              {
                '{{name}}, {{email}}, {{iptv_username}}, {{iptv_password}}, {{m3u_url}}, {{panel_url}}, {{expires_at}}, {{plan_name}}, {{whatsapp_support_number}}'
              }
            </code>
          </p>
        </div>

        <div className="flex items-center justify-end gap-2">
          {save.isPending && <span className="text-xs text-muted-foreground">Saving…</span>}
          {save.isSuccess && !save.isPending && (
            <span className="text-xs text-emerald-600">Saved.</span>
          )}
          {save.isError && (
            <span className="text-xs text-destructive">
              {(save.error as Error)?.message ?? 'Save failed'}
            </span>
          )}
          <Button onClick={() => save.mutate(draft)} disabled={save.isPending}>
            <Save className="mr-1.5 h-3.5 w-3.5" />
            Save messaging settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
